import { useCallback, useSyncExternalStore } from "react";
import {
  getInteractiveElementState,
  setInteractiveElementState,
  subscribeInteractiveElementState,
  NOT_STARTED,
  InteractiveElementStatusKind,
  type InteractiveElementScore,
  type InteractiveElementStatus,
} from "@/lib/interactiveElementState";

export interface UseInteractiveElementStateApi {
  status: InteractiveElementStatus;
  markInProgress: (partial: unknown, lastTaskIndex?: number) => void;
  complete: (score?: InteractiveElementScore, review?: unknown) => void;
  reset: () => void;
}

export const useInteractiveElementState = (
  contentElementId: string,
  enrollmentId: string | null
): UseInteractiveElementStateApi => {
  const subscribe = useCallback(
    (cb: () => void) => subscribeInteractiveElementState(cb),
    []
  );

  const getSnapshot = useCallback(
    () => getInteractiveElementState(contentElementId, enrollmentId),
    [contentElementId, enrollmentId]
  );

  const status = useSyncExternalStore(
    subscribe,
    getSnapshot,
    () => NOT_STARTED
  );

  const markInProgress = useCallback(
    (partial: unknown, lastTaskIndex?: number) => {
      setInteractiveElementState(contentElementId, enrollmentId, {
        kind: InteractiveElementStatusKind.InProgress,
        partial,
        lastTaskIndex,
      });
    },
    [contentElementId, enrollmentId]
  );

  const complete = useCallback(
    (score?: InteractiveElementScore, review?: unknown) => {
      const prev = getInteractiveElementState(contentElementId, enrollmentId);
      const prevAttempts =
        prev.kind === InteractiveElementStatusKind.Completed
          ? prev.attempts
          : 0;
      setInteractiveElementState(contentElementId, enrollmentId, {
        kind: InteractiveElementStatusKind.Completed,
        score,
        review,
        attempts: prevAttempts + 1,
        completedAt: new Date().toISOString(),
      });
    },
    [contentElementId, enrollmentId]
  );

  const reset = useCallback(() => {
    setInteractiveElementState(contentElementId, enrollmentId, NOT_STARTED);
  }, [contentElementId, enrollmentId]);

  return { status, markInProgress, complete, reset };
};
